import { cloneDeep } from "lodash";

import { contrastSensitivityTestConfiguration } from "@constants/testConstants/testSpecificConstants/contrastSensitivityConstants";
import { getVirtualAssistantVideoListForTest } from "@constants/virtualAssistanceConstants";
import {
  CollectedTestData,
  ContrastSensitivityData,
  EyeType,
  FPFNInfo,
  TestCategory,
  TestConfiguration,
  TestType,
} from "types/testTypes";

type CurrentTestDetails = Record<string, unknown>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function parseContrastSensitivityEye(value: unknown): EyeType | undefined {
  return typeof value === "string" && Object.values(EyeType).includes(value as EyeType)
    ? (value as EyeType)
    : undefined;
}

function parseContrastSensitivityTestType(value: unknown): TestType {
  if (value === TestType.Ascending) {
    return TestType.Ascending;
  }

  if (value === TestType.PelliRobson) {
    return TestType.PelliRobson;
  }

  return TestType.Threshold;
}

function normalizeContrastResult(value: unknown): ContrastSensitivityData | null {
  if (!isRecord(value)) {
    return null;
  }

  const grating = value.grating;
  const contrast = value.contrast;

  if (typeof grating !== "number" || typeof contrast !== "number") {
    return null;
  }

  return { grating, contrast };
}

function normalizeFpFnInfo(value: unknown): FPFNInfo | undefined {
  if (!isRecord(value) || !isRecord(value.fp) || !isRecord(value.fn)) {
    return undefined;
  }

  const fpVal = value.fp.val;
  const fpTotal = value.fp.total;
  const fnVal = value.fn.val;
  const fnTotal = value.fn.total;

  if (
    typeof fpVal !== "number" ||
    typeof fpTotal !== "number" ||
    typeof fnVal !== "number" ||
    typeof fnTotal !== "number"
  ) {
    return undefined;
  }

  return {
    fp: { val: fpVal, total: fpTotal },
    fn: { val: fnVal, total: fnTotal },
  };
}

export function buildContrastSensitivityResumeConfiguration(
  currentTestDetails: CurrentTestDetails,
  existingConfiguration?: TestConfiguration | null,
): TestConfiguration {
  const configuration =
    existingConfiguration?.name === TestCategory.ContrastSensitivity
      ? cloneDeep(existingConfiguration)
      : cloneDeep(contrastSensitivityTestConfiguration);

  configuration.name = TestCategory.ContrastSensitivity;
  configuration.testType = parseContrastSensitivityTestType(currentTestDetails.testType);

  const eyeType = parseContrastSensitivityEye(currentTestDetails.eye);
  if (eyeType) {
    configuration.eyeConfiguration = {
      ...configuration.eyeConfiguration,
      eyeType,
    };
  }

  if (configuration.virtualAssistanceConfig) {
    configuration.virtualAssistanceConfig = {
      ...configuration.virtualAssistanceConfig,
      videoInfos: getVirtualAssistantVideoListForTest(configuration),
    };
  }

  return configuration;
}

export function buildContrastSensitivityResumeCollectedData(
  currentTestDetails: CurrentTestDetails,
): CollectedTestData {
  const rawResults = isRecord(currentTestDetails.results) ? currentTestDetails.results : null;
  const contrastResultsSource = Array.isArray(rawResults?.contrastResults)
    ? rawResults.contrastResults
    : Array.isArray(currentTestDetails.contrastResults)
      ? currentTestDetails.contrastResults
      : [];
  const contrastResults = contrastResultsSource
    .map((result) => normalizeContrastResult(result))
    .filter((result): result is ContrastSensitivityData => result !== null);
  const fpFnInfo = normalizeFpFnInfo(rawResults?.fpFnInfo ?? currentTestDetails.fpFnInfo);

  return {
    contrastSensitivityOrPelliRobsonData: {
      ...(contrastResults.length > 0 && { contrastSensitivityDatas: contrastResults }),
      ...(fpFnInfo && { fpFnInfo }),
    },
  };
}
